import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Loader2, X } from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import { fetchWithAuth } from '@/lib/api/core'
import { useWorkspace } from '@/providers/workspace-provider'
import { useGoogleDriveAuth } from '@/hooks/use-google-drive-auth'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import {
  isDriveFileImportable,
  isDriveImagePreview,
  isDriveNativeTextPreview,
} from '@/lib/drive-importability'
import { getDrivePreviewUrl } from './drive-tree-adapter'
import { DriveNativeTextPreview } from './drive-native-text-preview'
import { DriveImagePreview } from './drive-image-preview'
import { FolderPickerDialog } from '@/components/features/folder-picker-dialog'
import {
  DRIVE_PREVIEW_OPEN_IMPORT_EVENT,
  GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT,
  driveImportFolderPath,
  organizationIdForFolderPath,
  type GoogleDriveImportCompleteDetail,
} from './gdoc-import-utils'

export type GoogleDrivePreviewTabData = {
  fileId: string
  name: string
  mimeType: string
  webViewLink?: string
  organizationId?: string
  importedDocumentId?: string
  isShortcut?: boolean
}

type GoogleDrivePreviewViewerProps = {
  data: GoogleDrivePreviewTabData
  onOpenDocument?: (documentId: string) => void
  onReplaceWithDocument?: (documentId: string) => void
}

export function GoogleDrivePreviewViewer({
  data,
  onOpenDocument,
  onReplaceWithDocument,
}: GoogleDrivePreviewViewerProps) {
  const { workspaces, activeWorkspaceIds } = useWorkspace()
  const [workspaceId, setWorkspaceId] = useState(
    data.organizationId ?? activeWorkspaceIds[0] ?? workspaces[0]?.id ?? '',
  )
  const { authStatus, isLoading: authLoading } = useGoogleDriveAuth(workspaceId || undefined)
  const [importing, setImporting] = useState(false)
  const [showImportPicker, setShowImportPicker] = useState(false)
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null)
  const [iframeBannerDismissed, setIframeBannerDismissed] = useState(false)

  const workspace = workspaces.find((w) => w.id === workspaceId)
  const connected = authStatus?.connected ?? false
  const previewUrl = getDrivePreviewUrl(data.fileId, data.mimeType)
  const nativeTextPreview = isDriveNativeTextPreview(data.mimeType, data.name)
  const imagePreview = isDriveImagePreview(data.mimeType, data.name)
  const emptyTitle = useMemo(() => {
    if (data.isShortcut && data.mimeType === 'application/vnd.google-apps.folder') {
      return 'Shortcut to a folder'
    }
    if (data.isShortcut) return 'Shortcut - no direct preview'
    return 'Preview not available'
  }, [data.isShortcut, data.mimeType])
  const emptyBody = useMemo(() => {
    if (data.isShortcut && data.mimeType === 'application/vnd.google-apps.folder') {
      return 'This Drive shortcut points to a folder. Open it in Google Drive to browse the original.'
    }
    if (data.isShortcut) {
      return 'This is a Drive shortcut pointing to a file in another location. Open it in Google Drive to view the original.'
    }
    return 'Google Drive does not allow this file type to be embedded. Open it directly in Google Drive instead.'
  }, [data.isShortcut, data.mimeType])

  // Fetch connected Google account email so we can hint the user which browser account to use
  useEffect(() => {
    if (!workspaceId || !connected) return
    void fetchWithAuth('/api/google-drive/accounts')
      .then((r) => r.ok ? r.json() as Promise<Array<{ id: string; email?: string; label?: string }>> : Promise.resolve([]))
      .then((accounts) => {
        if (accounts.length > 0) setConnectedEmail(accounts[0].email ?? null)
      })
      .catch(() => {/* non-critical */})
  }, [workspaceId, connected])

  const handleConnect = useCallback(async () => {
    if (!workspaceId) return
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/auth/url?organizationId=${encodeURIComponent(workspaceId)}`,
      )
      if (!res.ok) throw new Error('Failed to get auth URL')
      const body = await res.json() as { url?: string }
      if (body.url) {
        window.open(body.url, 'google-drive-oauth', 'width=500,height=650,popup')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start Google Drive connection')
    }
  }, [workspaceId])

  useEffect(() => {
    const openImport = () => {
      if (!isDriveFileImportable(data.mimeType)) {
        toast.error('This file type cannot be imported as a Maater document.')
        return
      }
      if (!connected) {
        void handleConnect()
        return
      }
      if (data.importedDocumentId) {
        onOpenDocument?.(data.importedDocumentId)
        return
      }
      setShowImportPicker(true)
    }
    window.addEventListener(DRIVE_PREVIEW_OPEN_IMPORT_EVENT, openImport)
    return () => window.removeEventListener(DRIVE_PREVIEW_OPEN_IMPORT_EVENT, openImport)
  }, [connected, data.importedDocumentId, data.mimeType, handleConnect, onOpenDocument])

  const handleImport = useCallback(async (folderPath?: string) => {
    const orgId =
      (folderPath ? organizationIdForFolderPath(folderPath, workspaces) : undefined) ?? workspaceId
    if (!orgId || importing) return
    if (!isDriveFileImportable(data.mimeType)) {
      toast.error('This file type cannot be imported as a Maater document.')
      return
    }
    if (!connected) {
      void handleConnect()
      return
    }
    if (orgId !== workspaceId) setWorkspaceId(orgId)
    const resolvedFolderPath =
      folderPath ??
      driveImportFolderPath(workspaces.find((w) => w.id === orgId)?.contentRoot)
    setImporting(true)
    try {
      const res = await fetchWithAuth('/api/google-drive/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId: orgId,
          files: [{ fileId: data.fileId, name: data.name, mimeType: data.mimeType }],
          folderPath: resolvedFolderPath,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`)
      }
      const result = await res.json() as {
        results?: Array<{
          documentId?: string
          status?: string
          error?: string
          title?: string
          tabCount?: number
          folderPath?: string
          tabDocIds?: Array<{ documentId: string; tabTitle: string }>
        }>
      }
      const firstResult = result.results?.[0]
      if (firstResult?.status === 'error') {
        toast.error(`Import failed: ${firstResult.error ?? 'Unknown error'}`)
        return
      }
      const documentId = firstResult?.documentId
      const tabCount = firstResult?.tabCount ?? 1
      if (documentId) {
        if (tabCount > 1) {
          toast.success(`Imported ${tabCount} tabs - opening first tab in Maater`)
        } else {
          toast.success('Imported - opening in Maater')
        }
        const detail: GoogleDriveImportCompleteDetail = {
          documentId,
          folderPath: firstResult?.folderPath ?? resolvedFolderPath,
          organizationId: orgId,
        }
        window.dispatchEvent(new CustomEvent(GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT, { detail }))
        if (onReplaceWithDocument) onReplaceWithDocument(documentId)
        else onOpenDocument?.(documentId)
      } else {
        toast.success(`Imported "${data.name}"`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }, [
    workspaceId,
    importing,
    connected,
    data.fileId,
    data.name,
    data.mimeType,
    handleConnect,
    onOpenDocument,
    onReplaceWithDocument,
    workspaces,
  ])

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={testIds.driveSidebar.drivePreview}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {previewUrl && connectedEmail && !iframeBannerDismissed && (
          <div className="flex shrink-0 items-start gap-2 border-b border-border bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">
              Preview uses your browser&apos;s Google session.{' '}
              If you see an access request, sign in to Google as{' '}
              <strong>{connectedEmail}</strong> in this browser.
            </span>
            <button
              onClick={() => setIframeBannerDismissed(true)}
              className="shrink-0 rounded p-0.5 hover:bg-amber-200/60 dark:hover:bg-amber-800/40"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex min-h-0 flex-1">
          {!connected && !authLoading ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <GoogleDriveIcon className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">Connect Google Drive</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                Connect {workspace?.name ?? 'this workspace'} to preview and import this file.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleConnect()}
                data-testid={testIds.driveSidebar.drivePreviewConnectButton}
              >
                Connect to Google
              </Button>
            </div>
          ) : authLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              className="min-h-0 w-full flex-1 border-0"
              title={data.name}
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          ) : nativeTextPreview ? (
            <div className="min-h-0 w-full flex-1 overflow-hidden">
              <DriveNativeTextPreview
                fileId={data.fileId}
                organizationId={workspaceId || undefined}
              />
            </div>
          ) : imagePreview ? (
            <div className="min-h-0 w-full flex-1 overflow-hidden">
              <DriveImagePreview
                fileId={data.fileId}
                name={data.name}
                organizationId={workspaceId || undefined}
              />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <GoogleDriveIcon className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
              <p className="max-w-xs text-xs text-muted-foreground">{emptyBody}</p>
              {data.webViewLink ? (
                <Button size="sm" variant="outline" asChild>
                  <a href={data.webViewLink} target="_blank" rel="noopener noreferrer">
                    Open in Google Drive
                  </a>
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <FolderPickerDialog
        open={showImportPicker}
        onOpenChange={setShowImportPicker}
        currentPath={workspaces.find((w) => w.id === workspaceId)?.contentRoot ?? ''}
        mode="import"
        workspaces={workspaces.map((w) => ({
          id: w.id,
          name: w.name,
          slug: w.slug,
          contentRoot: w.contentRoot ?? '',
        }))}
        onSelect={(folderPath) => {
          setShowImportPicker(false)
          void handleImport(folderPath)
        }}
      />
    </div>
  )
}
